  // components/FormatBar.js
  import React, { useState } from 'react';
  import { Bold, Italic, Code, List, Link, Sparkles, Tag, GitBranch } from 'lucide-react';
  import styles from './FormatBar.module.css';
  import { useEditor } from './contexts/EditorContext';
  import { useNotes } from './contexts/NotesContext';
  import useKeyPointsExtraction from '../hooks/useKeyPointsExtraction';
  import KeywordsModal from './KeywordsModal';
  import ExtractionModal from './ExtractionModal';
  

  const FormatBar = () => {
    const { editor } = useEditor();
    const { selectedNoteId, updateNoteContent, createSubnote, getSelectedNote } = useNotes();
    const { extractKeyPointsOnly, createStickiesFromResults, isProcessing } = useKeyPointsExtraction();
    const [showExtractionModal, setShowExtractionModal] = useState(false);
    const [showKeywordsModal, setShowKeywordsModal] = useState(false);
    
    
    
    const ToolbarButton = ({ onClick, children, active, disabled }) => (
      <button
        onClick={onClick}
        className={`${styles.formatButton} ${active ? styles.active : ''}`}
        disabled={disabled}
      >
        {children}
      </button>
    );

    // Function to handle keywords extraction
    const handleExtractKeywords = () => {
      if (!editor || !selectedNoteId) return;
      setShowKeywordsModal(true);
    };

    // Function to create a subnote
    const handleCreateSubnote = async () => {
      if (!selectedNoteId) return;
      
      // Get the current note to check if it's already a subnote
      const currentNote = getSelectedNote();
      if (!currentNote) return;
      
      // If current note is already a subnote, we don't allow creating a subnote from it
      // We'll only allow one level of nesting
      if (currentNote.parent_id) {
        alert("Cannot create a subnote from another subnote. Only one level of nesting is supported.");
        return;
      }
      
      try {
        await createSubnote(selectedNoteId);
      } catch (error) {
        console.error("Error creating subnote:", error);
        alert("Failed to create subnote. Please try again.");
      }
    };

    const handleFormat = (format) => {
      if (!editor) return;
      
      // If formatting a code block, handle it specially
      if (format === 'code-block') {
        const range = editor.getSelection();
        if (range) {
          const currentFormat = editor.getFormat(range);
          editor.format('code-block', !currentFormat['code-block']);
        }
      } else {
        editor.format(format, !editor.getFormat()[format]);
      }
      
      // After applying formatting, get the updated content and save it
      setTimeout(() => {
        if (editor && selectedNoteId) {
          const content = editor.root.innerHTML;
          updateNoteContent(selectedNoteId, content);
        }
      }, 10);
    };
    
    // Function to handle key points extraction
    const handleExtractKeyPoints = async (options = {}) => {
      const result = await extractKeyPointsOnly(options); 
      
      if (!result.success) {
        // Only show error messages
        alert(result.message);
      }
      return result;
    };

    return (
      <div className={styles.formatBar}>
        <div className={styles.formatBarSection}>
          <ToolbarButton onClick={() => handleFormat('bold')}>
            <Bold size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => handleFormat('italic')}>
            <Italic size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => handleFormat('code-block')}>
            <Code size={16} />
          </ToolbarButton>
        </div>

        <div className={styles.formatBarDivider} />

        <div className={styles.formatBarSection}>
          <ToolbarButton onClick={() => handleFormat('list', 'bullet')}>
            <List size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => handleFormat('link')}>
            <Link size={16} />
          </ToolbarButton>
        </div>
        
        {/* Add a spacer div to push the next section to the right */}
        <div style={{ flex: 1 }}></div>
        
        {/* Add Subnote button */}
        <div className={styles.formatBarSection}>
          <ToolbarButton onClick={handleCreateSubnote}>
            <GitBranch size={16} />
            <span style={{ marginLeft: '4px' }}>
              Add Subnote
            </span>
          </ToolbarButton>
        </div>
        
        <div className={styles.formatBarDivider} />
        
        {/* Key Points extraction button on the far right */}
        <div className={styles.formatBarSection}>
          <ToolbarButton 
            onClick={handleExtractKeywords}
          >
            <Tag size={16} />
            <span style={{ marginLeft: '4px' }}>
              Extract Keywords
            </span>
          </ToolbarButton>
         <ToolbarButton 
  onClick={() => setShowExtractionModal(true)} 
  disabled={isProcessing}
>
  <Sparkles size={16} />
  <span style={{ marginLeft: '4px' }}>
    {isProcessing ? 'Extracting...' : 'Extract Key Points'}
  </span>
</ToolbarButton>

</div>

{showKeywordsModal && (
  <KeywordsModal 
    onClose={() => setShowKeywordsModal(false)}
    editor={editor}
    selectedNoteId={selectedNoteId}
  />
)}

{showExtractionModal && (
  <ExtractionModal 
    onClose={() => setShowExtractionModal(false)}
    onExtract={handleExtractKeyPoints}
    onCreateStickies={createStickiesFromResults}
    isProcessing={isProcessing}
  />
)}
</div>
);
};

export default FormatBar;